/**
 * Embedding contract shared by the server and the browser.
 *
 * The game is shown inside a portal page (justaigame.com) in an iframe. Two
 * things must agree on who that portal is: the server, which says in a CSP
 * header who may frame it, and the browser, which says who it will listen to
 * and talk to over postMessage. They share one default list and one validator
 * from here. An override is still set twice (EMBED_PARENT_ORIGINS on the
 * server, VITE_EMBED_PARENT_ORIGINS at build time); whoever sets one must set
 * the other to match.
 */

/** Parents allowed to frame the game, and to be trusted over postMessage. */
export const DEFAULT_EMBED_PARENT_ORIGINS: readonly string[] = [
  "https://justaigame.com",
  "https://preview.justportfolio.pages.dev",
];

/**
 * Longest player assertion the server will keep. The portal signs a compact
 * token; anything larger than this is not one.
 */
export const PLAYER_ASSERTION_MAX_LENGTH = 4096;

/**
 * An exact https origin: lowercase host, optional port, no path, query,
 * credentials or wildcard. Deliberately a pattern rather than `new URL()` —
 * this package has no DOM or Node typings, and `URL` normalises (adds a slash,
 * lowercases) in ways that would let a sloppy value pass as "exact".
 */
const HTTPS_ORIGIN = /^https:\/\/[a-z0-9]+(?:-+[a-z0-9]+)*(?:\.[a-z0-9]+(?:-+[a-z0-9]+)*)*(?::[0-9]{1,5})?$/;

export function isHttpsOrigin(value: string): boolean {
  if (value.length > 253 || !HTTPS_ORIGIN.test(value)) return false;
  const port = value.split(":")[2];
  return port === undefined || (Number(port) >= 1 && Number(port) <= 65535);
}

export interface ParsedOrigins {
  /** Valid, de-duplicated origins in the order given. */
  origins: string[];
  /** Entries that were not an exact https origin. */
  invalid: string[];
}

/**
 * Split a space- or comma-separated list. An unset or blank value means "use
 * the defaults"; it never means "allow nobody", because that would silently
 * lock the portal out of its own game.
 */
export function parseEmbedOrigins(raw: string | undefined | null): ParsedOrigins {
  const entries = (raw ?? "")
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  if (entries.length === 0) return { origins: [...DEFAULT_EMBED_PARENT_ORIGINS], invalid: [] };

  const origins: string[] = [];
  const invalid: string[] = [];
  for (const entry of entries) {
    if (!isHttpsOrigin(entry)) invalid.push(entry);
    else if (!origins.includes(entry)) origins.push(entry);
  }
  return { origins, invalid };
}
